import React, { useState, useEffect, useRef } from 'react';
import { Bookmark, X, AlertTriangle, Check } from 'lucide-react';
import {
  RackReservation,
  EquipmentSlotItem,
  validateReservationPlacement,
} from '../../utils/rackReservation.logic';
import './RackReservationModal.css';

interface RackReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: Omit<RackReservation, 'id'>) => { success: boolean; error?: string } | Promise<{ success: boolean; error?: string }>;
  totalU: number;
  equipmentList: EquipmentSlotItem[];
  reservations: RackReservation[];
  initialSlot?: number;
  initialMount?: string;
  theme?: 'dark' | 'light';
}

export function RackReservationModal({
  isOpen,
  onClose,
  onSubmit,
  totalU,
  equipmentList,
  reservations,
  initialSlot = 1,
  theme: _theme = 'dark',
}: RackReservationModalProps) {
  const [uStart, setUStart] = useState<number>(initialSlot);
  const [uHeight, setUHeight] = useState<number>(2);
  const [tenant, setTenant] = useState<string>('mine');
  const [description, setDescription] = useState<string>('');
  const face = 'both';
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const element = dialogRef.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    element?.showModal();
    return () => {
      element?.close();
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleValidateAndSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);

    const validation = validateReservationPlacement(
      reservations,
      equipmentList,
      Number(uStart),
      Number(uHeight),
      totalU,
      undefined,
      face
    );

    if (!validation.valid) {
      setError(validation.conflictReason || 'Ошибка валидации бронирования');
      return;
    }

    setSaving(true);
    try {
    const result = await onSubmit({
      u_start: Number(uStart),
      u_height: Number(uHeight),
      tenant: tenant.trim() || 'mine',
      description: description.trim() || 'Резерв места',
      face,
    });

    if (!result.success) {
      setError(result.error || 'Не удалось сохранить бронирование');
      return;
    }

    onClose();
    } catch {
      setError('Подтверждение резервирования не получено. Проверьте резервы перед повтором.');
    } finally {
      setSaving(false);
    }
  };

  const endU = Number(uStart) + Number(uHeight) - 1;

  return (
    <dialog ref={dialogRef}
      className="rack-reservation-dialog"
      aria-labelledby="reservation-modal-title"
      onCancel={event => { event.preventDefault(); if (!saving) onClose(); }}
      onKeyDown={event => {
        event.stopPropagation();
        if (event.key !== 'Tab') return;
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button, input')].filter(el => !el.matches(':disabled'));
        const first = controls[0], last = controls[controls.length - 1];
        if (!first) event.preventDefault();
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}
    >
      <div
        className="min-w-0"
        style={{
          backgroundColor: 'var(--bg-panel)',
          borderColor: 'var(--border-color)',
          color: 'var(--text-primary)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b"
          style={{
            borderColor: 'var(--border-color)',
            backgroundColor: 'var(--bg-app)',
          }}
        >
          <div className="flex items-center gap-2">
            <Bookmark size={16} className="text-amber-500" aria-hidden="true" />
            <h3 id="reservation-modal-title" className="text-base font-semibold text-text-primary">
              Резервирование U
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded text-text-muted hover:text-text-primary transition-colors"
            aria-label="Закрыть"
            title="Закрыть"
            autoFocus
          >
            <X size={15} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleValidateAndSubmit} className="p-4 space-y-3.5 text-xs">
          {error && (
            <div role="alert" className="flex items-start gap-2 p-2.5 rounded border border-status-critical text-text-primary text-sm">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <fieldset disabled={saving} className="space-y-3.5 m-0 p-0 border-0 min-w-0">

          {/* Unit selection */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="reserve-start" className="block text-xs font-medium text-text-secondary mb-2">
                Начальный юнит (U)
              </label>
              <input
                type="number"
                id="reserve-start"
                min={1}
                max={totalU}
                value={uStart}
                onChange={(e) => setUStart(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>

            <div>
              <label htmlFor="reserve-height" className="block text-xs font-medium text-text-secondary mb-2">
                Высота (U)
              </label>
              <input
                type="number"
                id="reserve-height"
                min={1}
                max={totalU - uStart + 1}
                value={uHeight}
                onChange={(e) => setUHeight(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
              />
            </div>
          </div>

          <div className="text-[10px] text-amber-500 dark:text-amber-400/90 font-mono">
            Диапазон: <span className="font-bold">U{uStart}..U{endU}</span> ({uHeight}U)
          </div>

          {/* Tenant */}
          <div>
            <label htmlFor="reserve-tenant" className="block text-xs font-medium text-text-secondary mb-2">
              Владелец / Арендатор (Тенант)
            </label>
            <div className="flex gap-1.5 mb-1.5">
              {[
                { key: 'mine', label: 'Своя' },
                { key: 'colocation', label: 'Colocation' },
                { key: 'reserved', label: 'Буфер' },
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setTenant(chip.key)}
                  className={`px-2 py-0.5 text-[10px] rounded border transition-colors ${
                    tenant === chip.key
                      ? 'bg-amber-500/20 border-amber-500 text-amber-600 dark:text-amber-300 font-bold'
                      : 'border-border text-text-secondary hover:border-border-focus bg-bg-input'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              id="reserve-tenant"
              value={tenant}
              onChange={(e) => setTenant(e.target.value)}
              placeholder="Например, ООО Иннотех / Client A"
              className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          <p className="text-xs text-text-secondary">Резерв занимает всю глубину стойки.</p>

          {/* Description */}
          <div>
            <label htmlFor="reserve-description" className="block text-xs font-medium text-text-secondary mb-2">
              Назначение / Описание проекта
            </label>
            <input
              type="text"
              id="reserve-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Например, Резерв под проект K8s кластера"
              className="w-full px-2.5 py-1.5 rounded border border-border bg-bg-input text-text-primary text-xs focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-1.5 rounded text-xs transition-colors text-text-secondary hover:bg-bg-hover"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              aria-busy={saving}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded text-xs font-bold bg-amber-600 hover:bg-amber-500 text-white transition-colors shadow"
            >
              <Check size={14} />
              {saving ? 'Сохранение…' : 'Забронировать'}
            </button>
          </div>
          </fieldset>
        </form>
      </div>
    </dialog>
  );
}
